// signaling.js
import { ensurePeer, makeOffer } from './peer';

const pcState = (svc) => {
    const pc = svc.pc || {};

    return {
        iceGatheringState: pc.iceGatheringState,
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
        signalingState: pc.signalingState,
    };
};

const normalizeIncoming = (payload = {}) => ({
    callId: payload.callId,
    from: payload.from,
    to: payload.to,
    video: payload.video === true || payload.mode === 'video',
    mode: payload.mode || (payload.video ? 'video' : 'audio'),
    chatId: payload.chatId || null,
    status: payload.status || 'ringing',
    createdAt: payload.createdAt || null,
});

export function wireSignaling(svc) {
    const sock = svc.socket;

    const handleIncoming = (payload = {}) => {
        const data = normalizeIncoming(payload);

        if (!data.callId || !data.from) return;
        if (svc.state !== 'idle' && svc.callId !== data.callId) return;
        if (svc._lastIncomingCallId === data.callId && svc.state === 'ringing') return;

        svc._lastIncomingCallId = data.callId;
        svc.callId = data.callId;
        svc.partner = data.from;
        svc.chatId = data.chatId;
        svc.video = !!data.video;
        svc.mode = data.mode || (data.video ? 'video' : 'audio');
        svc.role = 'callee';
        svc.state = 'ringing';

        svc._deriveLeaderFlags();

        svc.emitter.emit('incoming', data);
        svc.emitter.emit('ring:start', { mode: 'incoming' });
    };

    const handleAccepted = async (payload = {}) => {
        const callId = payload.callId;

        if (!callId || svc.callId !== callId) return;
        if (svc._lastAcceptedCallId === callId && svc.pc?.localDescription) return;

        svc._lastAcceptedCallId = callId;

        if (svc.role !== 'caller') {
            return;
        }

        if (svc.state !== 'ringing' && svc.state !== 'dialing') {
            return;
        }

        await makeOffer(svc);
    };

    sock.on('webrtc:ice-config', (cfg) => {
        if (cfg && Array.isArray(cfg.iceServers)) {
            svc.iceServers = cfg.iceServers;
        }

        svc.emitter.emit('ice-config', cfg);
    });

    sock.on('call:invite', handleIncoming);
    sock.on('call:incoming', handleIncoming);

    sock.on('call:accept', handleAccepted);
    sock.on('call:accepted', handleAccepted);

    sock.on('call:offer', async ({ callId, sdp, from }) => {
        if (!callId || !sdp) return;
        if (svc.callId && svc.callId !== callId) return;

        if (from && !svc.partner) {
            svc.partner = from;
        }

        if (!svc.callId) {
            svc.callId = callId;
        }

        await ensurePeer(svc);

        const desc = new svc.media.RTCSessionDescription(sdp);
        const offerCollision = desc.type === 'offer' && (svc.makingOffer || svc.pc.signalingState !== 'stable');

        svc.ignoreOffer = !svc.isPolite && offerCollision;

        if (svc.ignoreOffer) {
            return;
        }

        try {
            if (offerCollision) {
                await svc.pc.setLocalDescription({ type: 'rollback' }).catch(() => { });
            }

            await svc.pc.setRemoteDescription(desc);

            (svc._pendingCandidates || []).forEach((candidate) => {
                svc.pc.addIceCandidate(new svc.media.RTCIceCandidate(candidate)).catch(() => { });
            });

            svc._pendingCandidates = [];

            if (desc.type === 'offer') {
                const answer = await svc.pc.createAnswer();

                await svc.pc.setLocalDescription(answer);

                svc.socket.emit('call:answer', {
                    callId: svc.callId,
                    to: svc.partner || from,
                    sdp: answer,
                });
            }

            if (svc.state !== 'in-call') {
                svc.state = 'connecting';
                svc.emitter.emit('connecting');
            }

            svc.emitter.emit('ring:stop');

            console.log('[calls][pc] setRemote ok', pcState(svc));
        } catch (error) {
            console.warn('[calls][pc] remote sdp failed', error?.message || error);
        }
    });

    sock.on('call:answer', async ({ callId, sdp }) => {
        if (!callId || !sdp) return;
        if (svc.callId && svc.callId !== callId) return;

        try {
            if (!svc.pc) return;

            if (svc.pc.signalingState !== 'have-local-offer') {
                return;
            }

            await svc.pc.setRemoteDescription(new svc.media.RTCSessionDescription(sdp));

            (svc._pendingCandidates || []).forEach((candidate) => {
                svc.pc.addIceCandidate(new svc.media.RTCIceCandidate(candidate)).catch(() => { });
            });

            svc._pendingCandidates = [];

            if (svc.state !== 'in-call') {
                svc.state = 'connecting';
                svc.emitter.emit('connecting');
            }

            svc.emitter.emit('ring:stop');

            console.log('[calls][pc] answer ok', pcState(svc));
        } catch (error) {
            console.warn('[calls][pc] answer failed', error?.message || error);
        }
    });

    sock.on('call:candidate', async ({ callId, candidate }) => {
        if (!candidate) return;
        if (callId && svc.callId && svc.callId !== callId) return;

        try {
            if (!svc.pc || !svc.pc.remoteDescription) {
                svc._pendingCandidates = svc._pendingCandidates || [];
                svc._pendingCandidates.push(candidate);
                return;
            }

            await svc.pc.addIceCandidate(new svc.media.RTCIceCandidate(candidate));
        } catch (error) {
            console.warn('[calls][pc] addIceCandidate failed', error?.message || error);
        }
    });

    sock.on('call:toggle', (payload = {}) => {
        svc.emitter.emit('remote-toggle', payload);
    });

    const handleRejected = ({ callId, reason } = {}) => {
        if (callId && svc.callId && callId !== svc.callId) return;
        svc._cleanup(reason || 'rejected');
    };

    const handleCancelled = ({ callId, reason } = {}) => {
        if (callId && svc.callId && callId !== svc.callId) return;
        svc._cleanup(reason || 'cancelled');
    };

    const handleEnded = ({ callId, reason } = {}) => {
        if (callId && svc.callId && callId !== svc.callId) return;
        svc._cleanup(reason || 'ended');
    };

    sock.on('call:reject', handleRejected);
    sock.on('call:rejected', handleRejected);
    sock.on('call:cancel', handleCancelled);
    sock.on('call:cancelled', handleCancelled);
    sock.on('call:end', handleEnded);
    sock.on('call:busy', ({ callId } = {}) => {
        if (callId && svc.callId && callId !== svc.callId) return;
        svc._cleanup('busy');
    });
}